import { type ReactElement } from 'react';
import { Box, Button, Icon } from '@ff-it/ui';
import cx from 'clsx';
import type { Attachment } from '../types';
import { fileCard, fileDetails, fileIcon, fileLink, fileList, fileMeta, fileName, removed } from './FileContainer.css';

interface FileContainerProps {
  files: Attachment[];
  markRemove?: (id: number) => void;
  disabled?: boolean;
}

const fileIcons: Record<string, string> = {
  pdf: 'file-pdf',
  doc: 'file-word',
  docx: 'file-word',
  xls: 'file-excel',
  xlsx: 'file-excel',
  png: 'file-image',
  jpg: 'file-image',
  jpeg: 'file-image',
  gif: 'file-image',
  zip: 'file-zipper',
};

function fileExtension(filename: string): string | null {
  const separator = filename.lastIndexOf('.');
  return separator > 0 && separator < filename.length - 1 ? filename.slice(separator + 1).toLowerCase() : null;
}

function FileContainer({ files, markRemove, disabled }: FileContainerProps): ReactElement | null {
  if (files.length === 0) {
    return null;
  }

  const isRemoved = markRemove === undefined;

  return (
    <Box as="ul" className={fileList}>
      {files.map(({ file, filename, id }) => {
        const extension = fileExtension(filename);
        return (
          <Box as="li" className={cx(fileCard, isRemoved && removed)} key={id}>
            <Box as="a" className={fileLink} href={file} target="_blank" rel="noopener noreferrer">
              <Icon className={fileIcon} icon={extension ? (fileIcons[extension] ?? 'file') : 'file'} size="lg" />
              <Box className={fileDetails}>
                <Box className={fileName}>{filename}</Box>
                <Box className={fileMeta}>{extension ? `${extension.toUpperCase()} file` : 'Attachment'}</Box>
              </Box>
            </Box>
            {markRemove ? (
              disabled ? null : (
                <Button
                  variant="ghost"
                  size="xxs"
                  type="button"
                  title={`Remove ${filename}`}
                  onClick={() => markRemove(id)}
                  icon="trash"
                />
              )
            ) : (
              <Button variant="ghost" size="xxs" type="button" title={`${filename} removed`} disabled icon="trash" />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default FileContainer;
